import type { ChatMessage } from '../../services/chatService';
import type { SupervisorButton } from '../../types/supervisorButtons';

export interface SuggestedAgentsEventDetail {
  agents: string[];
  buttons: SupervisorButton[];
  projectId: string;
  messageId: string;
  sessionId?: string | null;
  promptScope?: Record<string, unknown> | null;
  aiContent?: string | null;
}

export interface SuggestionContextLike {
  projectId: string;
  sessionId?: string | null;
}

export const suggestionMatchesContext = (
  suggestion: SuggestionContextLike | null,
  projectId?: string | null,
  sessionId?: string | null,
): boolean => {
  if (!suggestion) return false;
  if (projectId && suggestion.projectId !== projectId) return false;
  if (suggestion.sessionId && suggestion.sessionId !== sessionId) return false;

  return true;
};

export const suggestedAgentsEventKey = (
  detail: Pick<SuggestedAgentsEventDetail, 'projectId' | 'sessionId' | 'messageId'>,
): string => [detail.projectId, detail.sessionId ?? '', detail.messageId].join(':');

export const dispatchSuggestedAgentsEvent = (detail: SuggestedAgentsEventDetail): void => {
  window.dispatchEvent(new globalThis.CustomEvent('suggested_agents', { detail }));
};

const mergeButtons = (
  currentButtons: SupervisorButton[] | null | undefined,
  incomingButtons: SupervisorButton[],
): SupervisorButton[] => {
  const merged = Array.isArray(currentButtons) ? [...currentButtons] : [];
  const seen = new Set(merged.map((button) => button.key));

  incomingButtons.forEach((button) => {
    if (seen.has(button.key)) return;
    merged.push(button);
    seen.add(button.key);
  });

  return merged;
};

const suggestionMatchesMessage = (
  detail: SuggestedAgentsEventDetail,
  message: ChatMessage,
): boolean => (
  message.id === detail.messageId
  && (!message.project_id || message.project_id === detail.projectId)
  && (!detail.sessionId || !message.session_id || message.session_id === detail.sessionId)
);

const mergeSuggestionIntoMessage = (
  message: ChatMessage,
  buttons: SupervisorButton[],
): ChatMessage => {
  const mergedButtons = mergeButtons(message.buttons, buttons);
  const allowedButtons = Array.from(new Set([
    ...(message.allowed_buttons ?? []),
    ...mergedButtons.map((button) => button.key),
  ]));

  return {
    ...message,
    phase: message.phase ?? 'agent_suggestion',
    allowed_buttons: allowedButtons,
    buttons: mergedButtons,
  };
};

export const mergePendingSuggestedActionMetadata = (
  messages: ChatMessage[],
  pending: ReadonlyMap<string, SuggestedAgentsEventDetail>,
): { messages: ChatMessage[]; matchedKeys: string[] } => {
  if (pending.size === 0) return { messages, matchedKeys: [] };

  const matchedKeys: string[] = [];
  let changed = false;
  const nextMessages = messages.map((message) => {
    let nextMessage = message;

    pending.forEach((detail, key) => {
      if (detail.buttons.length === 0 || !suggestionMatchesMessage(detail, message)) return;
      nextMessage = mergeSuggestionIntoMessage(nextMessage, detail.buttons);
      matchedKeys.push(key);
      changed = true;
    });

    return nextMessage;
  });

  return {
    messages: changed ? nextMessages : messages,
    matchedKeys,
  };
};

const RESTORABLE_UTILITY_BUTTON_KEYS = ['start_build'];
const RESTORABLE_UTILITY_BUTTON_ACTIONS: SupervisorButton['action'][] = ['start_build'];

export const isRestorableSupervisorButton = (button: SupervisorButton): boolean => (
  button.kind === 'action'
  || RESTORABLE_UTILITY_BUTTON_KEYS.includes(button.key)
  || RESTORABLE_UTILITY_BUTTON_ACTIONS.includes(button.action)
);
